import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePlan } from '../../context/PlanContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
Building2, Users, Mail, UserPlus, Shield, X, Loader2,
    CheckCircle, Trash2, Globe, Plus, RefreshCw, Star, AlertCircle, ClipboardCopy,
Search, Edit3, KeyRound, MailCheck, Calendar, Briefcase,
    FileText, Ban, ShieldCheck, ShieldOff, MoreHorizontal, Eye, Sparkles, Clock
} from 'lucide-react';
import AiPrivacyControls from '../../components/admin/AiPrivacyControls';
import OwnerQuotaTimezoneSetting from '../../components/admin/OwnerQuotaTimezoneSetting';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import UserFormDialog from '../../components/admin/UserFormDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import RepProfilePanel from '../../components/RepProfilePanel';
import UserProfileMdPanel from '../../components/UserProfileMdPanel';
import AdminLayout from './AdminLayout';
import { isOrgAdminUser, computeEffectiveAccess } from '../../utils/orgRoles';
import { errorText } from '../../lib/apiErrors';
import { isStepUpRequired, stepUpBlockedNotice, stepUpThenReload, STEP_UP_ACTION_LABEL } from '../../lib/stepUp';
import { pillarLabelOf } from '../../utils/roleLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = { admin: 'bg-stone-800 text-white', manager: 'bg-stone-200 text-stone-700', sales_rep: 'bg-stone-100 text-stone-600' };

export default function AdminOrganizationPage() {
    const { getAuthHeader, user } = useAuth();
    const { plan, planLabel, checkLimit, loading: planLoading, error: planError, refreshPlan } = usePlan();

    // ─── OrganizationSettings state ──────────────────────────────────────
    const [organization, setOrganization] = useState(null);
    const [stats, setStats] = useState(null);
    const [invites, setInvites] = useState([]);
    const [orgLoading, setOrgLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviting, setInviting] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    // Custom Domains state
    const [domains, setDomains] = useState([]);
    const [newDomain, setNewDomain] = useState('');
    const [addingDomain, setAddingDomain] = useState(false);
    const [verifyingId, setVerifyingId] = useState(null);
    const [expandedDomainId, setExpandedDomainId] = useState(null);

    // ─── TeamMembersTab state ────────────────────────────────────────────
    const [teamUsers, setTeamUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [teamLoading, setTeamLoading] = useState(true);
    // #854: the team list was refused for want of the code factor (the
    // "Confirm it's you" prompt was cancelled). Shown instead of an empty list.
    const [teamBlocked, setTeamBlocked] = useState(false);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [showEdit, setShowEdit] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [showResetPw, setShowResetPw] = useState(false);
    // Issue #565 — admin-assisted two-step reset. Separate state from the
    // password dialog: they are adjacent in the menu but never open together.
    const [showResetMfa, setShowResetMfa] = useState(false);
    const [resetMfaUser, setResetMfaUser] = useState(null);
    const [resetMfaReason, setResetMfaReason] = useState('');
    const [resettingMfa, setResettingMfa] = useState(false);
    const [resetPwUser, setResetPwUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showRepProfile, setShowRepProfile] = useState(false);
    const [repProfileUser, setRepProfileUser] = useState(null);
    const [showProfileMd, setShowProfileMd] = useState(false);
    const [profileMdUser, setProfileMdUser] = useState(null);

    // ─── Load Organization Data ─────────────────────────────────────────
    const loadOrganizationData = async () => {
        setOrgLoading(true);
        try {
            const [orgRes, statsRes, invitesRes, domainsRes] = await Promise.all([
                axios.get(`${API}/organizations/current`, { headers: getAuthHeader() }),
                axios.get(`${API}/organizations/stats`, { headers: getAuthHeader() }),
                axios.get(`${API}/organizations/invites`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
                axios.get(`${API}/organizations/domains`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
            ]);
            setOrganization(orgRes.data);
            setStats(statsRes.data);
            setInvites(invitesRes.data);
            setDomains(domainsRes.data);
        } catch (err) {
            console.error('Failed to load org data:', err);
        } finally {
            setOrgLoading(false);
        }
    };

    // ─── Load Team Data ──────────────────────────────────────────────────
    const loadTeam = async (signal) => {
        setTeamLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterRole) params.set('role', filterRole);
            if (filterStatus) params.set('status', filterStatus);
            if (filterGroup) params.set('group_id', filterGroup);
            const [usersRes, groupsRes] = await Promise.all([
                axios.get(`${API}/admin/users?${params}`, { headers: getAuthHeader(), signal }),
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader(), signal }),
            ]);
            setTeamUsers(usersRes.data);
            setGroups(groupsRes.data);
            setTeamBlocked(false);
        } catch (err) {
            if (axios.isCancel?.(err) || err.name === 'CanceledError') return;
            const blocked = isStepUpRequired(err);
            setTeamBlocked(blocked);
            if (!blocked) toast.error(errorText(err, 'Failed to load team'));
        }
        finally { setTeamLoading(false); }
    };

    // mount-only: loadOrganizationData reads no component state (setters + module-level only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadOrganizationData(); }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadTeam(controller.signal);
        return () => controller.abort();
        // loadTeam reads only filter state already listed below; re-runs on filter change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterRole, filterStatus, filterGroup]);

    // ─── Team Members Handlers ──────────────────────────────────────────
    const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name, role: u.role, is_admin: u.is_admin, group_ids: u.groups?.map(g => g.group_id) || [], picture: u.picture || '', org_role: u.org_role || (u.is_admin ? 'org_admin' : 'member'), pillar_roles: u.pillar_roles || {}, is_agent_developer: u.is_agent_developer || false, contact_visibility: u.contact_visibility || 'all' }); setShowEdit(true); };
    const handleEdit = async () => {
        try {
            const headers = getAuthHeader();
            await axios.put(`${API}/admin/users/${editUser.user_id}`, { name: editForm.name, group_ids: editForm.group_ids, picture: editForm.picture, is_agent_developer: editForm.is_agent_developer, role: editForm.role || 'sales_rep' }, { headers });
            await axios.patch(`${API}/admin/users/${editUser.user_id}/org-role`, { org_role: editForm.org_role }, { headers });
            if ((editForm.contact_visibility || 'all') !== (editUser.contact_visibility || 'all')) {
                await axios.patch(`${API}/admin/users/${editUser.user_id}/contact-visibility`, { contact_visibility: editForm.contact_visibility || 'all' }, { headers });
            }
            if (editForm.org_role === 'member' && editForm.pillar_roles) {
                await axios.patch(`${API}/admin/users/${editUser.user_id}/pillar-roles`, { pillar_roles: editForm.pillar_roles }, { headers });
            }
            // Honest post-save verdict (#558 follow-on): when the save carries
            // org-admin power via a department role, say so instead of the bare
            // confirmation the admin just watched get overwritten elsewhere.
            const eff = computeEffectiveAccess(editForm.org_role, editForm.pillar_roles);
            if (editForm.org_role === 'member' && eff.isOrgAdmin && eff.origin === 'pillar') {
                const deptRole = (editForm.pillar_roles || {})[eff.driverPillarId]?.role === 'developer' ? 'Developer' : 'Admin';
                toast.warning(`User updated — ${pillarLabelOf(eff.driverPillarId)} ${deptRole} grants full organization admin (work role stored as Admin)`);
            } else {
                toast.success('User updated');
            }
            setShowEdit(false); loadTeam();
        }
        catch (err) { toast.error(errorText(err, 'Failed')); }
    };
    // Issue #565. The server refuses a self-reset and cross-org targets; the
    // 10-character reason floor is validated in both places on purpose — here so
    // the operator is told before the round trip, and server-side because that
    // is where the audit row is written.
    const handleResetMfa = async () => {
        if (resetMfaReason.trim().length < 10) return toast.error('Please give a reason of at least 10 characters');
        setResettingMfa(true);
        try {
            await axios.post(`${API}/admin/users/${resetMfaUser.user_id}/reset-mfa`, { reason: resetMfaReason.trim() }, { headers: getAuthHeader() });
            toast.success(`Two-step verification reset for ${resetMfaUser.email}`);
            setShowResetMfa(false); setResetMfaReason(''); loadTeam();
        }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
        finally { setResettingMfa(false); }
    };
    const handleViewAs = async (u) => {
        try {
            const res = await axios.post(`${API}/admin/view-as/${u.user_id}`, {}, { headers: getAuthHeader() });
            const { access_token, viewed_user } = res.data;
            const viewUrl = `${window.location.origin}/dashboard?impersonate_token=${access_token}&impersonate_name=${encodeURIComponent('View As: ' + (viewed_user.name || viewed_user.email))}`;
            window.open(viewUrl, '_blank');
            toast.success(`Viewing as ${viewed_user.name || viewed_user.email} in a new tab`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to view as user'); }
    };
    const toggleActive = async (u) => {
        try { const res = await axios.put(`${API}/admin/users/${u.user_id}/toggle-active`, {}, { headers: getAuthHeader() }); toast.success(res.data.message); loadTeam(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const handleResetPw = async () => {
        if (!newPassword || newPassword.length < 8) return toast.error('Password must be at least 8 characters');
        try { await axios.put(`${API}/admin/users/${resetPwUser.user_id}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() }); toast.success('Password reset'); setShowResetPw(false); setNewPassword(''); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    // ─── Organization Invite Handler ────────────────────────────────────
    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail) return;
        setInviting(true);
        try {
            const res = await axios.post(`${API}/organizations/invites`, {
                email: inviteEmail,
                role: inviteRole
            }, { headers: getAuthHeader() });
            toast.success(`Invite sent to ${inviteEmail}`);
            setInviteEmail('');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send invite');
        } finally {
            setInviting(false);
        }
    };

    const cancelInvite = async (inviteId) => {
        try {
            await axios.delete(`${API}/organizations/invites/${inviteId}`, { headers: getAuthHeader() });
            toast.success('Invite revoked');
            loadOrganizationData();
        } catch {
            toast.error('Failed to revoke invite');
        }
    };

    // Resend a pending invitation email (#568 D7): same single-use code,
    // refreshed 7-day window, re-relayed through the Email Broker.
    const handleResendInvite = async (inviteId) => {
        setResendingId(inviteId);
        try {
            const res = await axios.post(`${API}/organizations/invites/${inviteId}/resend`, {}, { headers: getAuthHeader() });
            toast.success(res.data.email_sent ? 'Invite resent' : 'Resend requested — the email broker did not confirm delivery');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to resend invite');
        } finally {
            setResendingId(null);
        }
    };

    // ─── Custom Domain Handlers ─────────────────────────────────────────
    const handleAddDomain = async (e) => {
        e.preventDefault();
        if (!newDomain.trim()) return;
        setAddingDomain(true);
        try {
            const res = await axios.post(`${API}/organizations/domains`, { domain: newDomain.trim() }, { headers: getAuthHeader() });
            toast.success(`Domain ${newDomain} added! Follow the verification instructions below.`);
            setNewDomain('');
            setExpandedDomainId(res.data.domain_id);
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add domain');
        } finally {
            setAddingDomain(false);
        }
    };

    const handleVerifyDomain = async (domainId) => {
        setVerifyingId(domainId);
        try {
            const res = await axios.post(`${API}/organizations/domains/${domainId}/verify`, {}, { headers: getAuthHeader() });
            if (res.data.verified) {
                toast.success('Domain verified successfully!');
            } else {
                toast.warning(res.data.message || 'Verification failed. Check your DNS records.');
            }
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
        } finally {
            setVerifyingId(null);
        }
    };

    const handleDeleteDomain = async (domainId, domainName) => {
        if (!window.confirm(`Remove domain "${domainName}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/organizations/domains/${domainId}`, { headers: getAuthHeader() });
            toast.success('Domain removed');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove domain');
        }
    };

    const handleSetPrimary = async (domainId) => {
        try {
            await axios.post(`${API}/organizations/domains/${domainId}/set-primary`, {}, { headers: getAuthHeader() });
            toast.success('Primary domain updated');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set primary domain');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getDomainStatusBadge = (status) => {
        const styles = {
            pending: 'bg-amber-50 text-amber-700 border-amber-200',
            verifying: 'bg-blue-50 text-blue-700 border-blue-200',
            verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            failed: 'bg-red-50 text-red-700 border-red-200',
        };
        const labels = {
            pending: 'Pending Verification',
            verifying: 'Verifying...',
            verified: 'Verified',
            active: 'Active',
            failed: 'Failed',
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
                {status === 'active' || status === 'verified' ? <CheckCircle className="w-3 h-3" /> : status === 'pending' ? <AlertCircle className="w-3 h-3" /> : null}
                {labels[status] || status}
            </span>
        );
    };

    // Issue #558 — single admin-equivalent predicate (owner | org_admin | is_admin).
    // The old owner/admin literal comparison hid the invite form, the AI-privacy
    // panel and the Custom Domains panel from promoted org_admins.
    const canManageMembers = isOrgAdminUser(user);

    if (planLoading) {
        return (
            <AdminLayout title="Organization" description="Manage your organization, team members, and custom domains">
                <div className="flex items-center justify-center py-12" data-testid="plan-loading">
                    <Loader2 className="w-6 h-6 text-stone-700 animate-spin" />
                </div>
            </AdminLayout>
        );
    }

    if (planError) {
        return (
            <AdminLayout title="Organization" description="Manage your organization, team members, and custom domains">
                <div className="text-center py-12" data-testid="plan-error">
                    <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-stone-900">Failed to load plan</h3>
                    <p className="text-sm text-stone-500 mt-1">Could not load your subscription plan.</p>
                    <Button variant="outline" className="mt-4" onClick={refreshPlan} data-testid="retry-plan-btn">
                        <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
                    </Button>
                </div>
            </AdminLayout>
        );
    }

    if (orgLoading) {
        return (
            <AdminLayout title="Organization" description="Manage your organization, team members, and custom domains">
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-stone-700 animate-spin" />
                </div>
            </AdminLayout>
        );
    }

    if (!organization || !organization.org_id) {
        return (
            <AdminLayout title="Organization" description="Manage your organization, team members, and custom domains">
                <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-stone-900">No Organization</h3>
                    <p className="text-sm text-stone-500 mt-1">You're not part of any organization yet.</p>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Organization" description="Manage your organization, team members, and custom domains">
            <div className="space-y-6">
                {/* ─── Section 1: Organization Header ─────────────────────── */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-stone-1000 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                            {organization.name?.charAt(0) || 'O'}
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-stone-900">{organization.name}</h2>
                            <p className="text-sm text-stone-500">{organization.domain || 'No domain set'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                            plan === 'pro' ? 'bg-stone-100 text-stone-900' :
                            plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                            'bg-stone-100 text-stone-600'
                        }`}>
                            {planLabel}
                        </span>
                    </div>
                </div>

                {/* ─── Section 2: Usage Stats ──────────────────────────────── */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(() => {
                            const userLimit = checkLimit('users');
                            const contactLimit = checkLimit('contacts');
                            const accountLimit = checkLimit('accounts');
                            const dealLimit = checkLimit('deals');
                            return (
                                <>
                                <div className="p-4 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-stone-500">Users</span>
                                        <Users className="w-4 h-4 text-stone-400" />
                                    </div>
                                    <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.users}</p>
                                    <p className="text-xs text-stone-400">{userLimit.unlimited ? 'Unlimited' : `of ${userLimit.limit}`}</p>
                                </div>
                                <div className="p-4 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-stone-500">Contacts</span>
                                        <Users className="w-4 h-4 text-stone-400" />
                                    </div>
                                    <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.contacts}</p>
                                    <p className="text-xs text-stone-400">{contactLimit.unlimited ? 'Unlimited' : `of ${contactLimit.limit}`}</p>
                                </div>
                                <div className="p-4 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-stone-500">Companies</span>
                                        <Building2 className="w-4 h-4 text-stone-400" />
                                    </div>
                                    <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.accounts}</p>
                                    <p className="text-xs text-stone-400">{accountLimit.unlimited ? 'Unlimited' : `of ${accountLimit.limit}`}</p>
                                </div>
                                <div className="p-4 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-stone-500">Deals</span>
                                        <Building2 className="w-4 h-4 text-stone-400" />
                                    </div>
                                    <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.deals}</p>
                                    <p className="text-xs text-stone-400">{dealLimit.unlimited ? 'Unlimited' : `of ${dealLimit.limit}`}</p>
                                </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* ─── Section 3: Team Members Table ──────────────────────── */}
                <div>
                    <div className="flex items-center gap-3 flex-wrap mb-4">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search team members..."
                                className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" data-testid="team-search-input" />
                        </div>
                        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-role-filter">
                            <option value="">All Roles</option><option value="admin">Admin</option><option value="manager">Manager</option><option value="sales_rep">Sales Rep</option>
                        </select>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-status-filter">
                            <option value="">All</option><option value="active">Active</option><option value="inactive">Suspended</option>
                        </select>
                        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-group-filter">
                            <option value="">All Groups</option>
                            {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                        </select>
                    </div>

                    {teamLoading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-500 animate-spin" /></div>
                    ) : teamBlocked ? (
                        <div className="text-center py-12" data-testid="org-team-step-up">
                            <ShieldCheck className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                            <p className="text-stone-500">{stepUpBlockedNotice().text}</p>
                            {/* Ask for the code, then reload: the invites and
                                domains panels were refused too. No button while
                                viewing as someone, where it can't help. */}
                            {stepUpBlockedNotice().actionable && (
                                <Button variant="outline" size="sm" className="mt-3" data-testid="org-team-step-up-btn"
                                    onClick={() => stepUpThenReload()}>
                                    {STEP_UP_ACTION_LABEL}
                                </Button>
                            )}
                        </div>
                    ) : teamUsers.length === 0 ? (
                        <div className="text-center py-12"><Users className="w-12 h-12 text-stone-200 mx-auto mb-3" /><p className="text-stone-500">No team members found</p></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="team-table">
                                <thead><tr className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider border-b border-stone-200">
                                    <th className="px-4 py-3">Member</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Integrations</th><th className="px-4 py-3">Two-step</th><th className="px-4 py-3">Groups</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Last Login</th><th className="px-4 py-3 text-right">Actions</th>
                                </tr></thead>
                                <tbody className="divide-y divide-stone-100">
                                    {teamUsers.map(u => (
                                        <tr key={u.user_id} className="hover:bg-stone-50 transition-colors" data-testid={`team-row-${u.user_id}`}>
                                            <td className="px-4 py-3"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${u.is_active ? 'bg-stone-200 text-stone-700' : 'bg-stone-100 text-stone-400'}`}>{u.name?.charAt(0)?.toUpperCase() || '?'}</div><div><div className="text-sm font-medium text-stone-900">{u.name || 'Unnamed'}</div><div className="text-xs text-stone-400">{u.email}</div></div></div></td>
                                            <td className="px-4 py-3">{isOrgAdminUser(u) ? (
                                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-stone-900 text-white">Org Admin</span>
                                            ) : u.pillar_roles && Object.keys(u.pillar_roles).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">{Object.entries(u.pillar_roles).slice(0, 3).map(([pid, pr]) => (
                                                    <span key={pid} className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${pr.read_only ? 'bg-amber-50 text-amber-700' : roleColors[pr.role] || roleColors.sales_rep}`}>{pid.replace('custom_mod_', '').slice(0, 5)}: {pr.role}{pr.read_only ? ' (RO)' : ''}</span>
                                                ))}{Object.keys(u.pillar_roles).length > 3 && <span className="text-[10px] text-stone-400">+{Object.keys(u.pillar_roles).length - 3}</span>}</div>
                                            ) : (
                                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColors[u.role] || roleColors.sales_rep}`}>{u.role?.replace('_', ' ')}</span>
                                            )}</td>
                                            <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}><span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />{u.is_active ? 'Active' : 'Suspended'}</span></td>
                                            <td className="px-4 py-3"><div className="flex items-center gap-2">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${u.gmail_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-400'}`} title={u.gmail_connected ? `Gmail: ${u.gmail_email}` : 'Not connected'}><MailCheck className="w-3 h-3" /> Email</span>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${u.calendar_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-400'}`}><Calendar className="w-3 h-3" /> Cal</span>
                                            </div></td>
                                            <td className="px-4 py-3">{u.mfa_enabled ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700" title={u.mfa_enrolled_at ? `Enrolled ${new Date(u.mfa_enrolled_at).toLocaleDateString()}` : 'Enrolled'}><ShieldCheck className="w-3 h-3" /> On</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-400"><Shield className="w-3 h-3" /> Off</span>
                                            )}</td>
                                            <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{u.groups?.length > 0 ? u.groups.map(g => <span key={g.group_id} className="inline-flex px-1.5 py-0.5 text-xs bg-stone-100 text-stone-600 rounded">{g.name}</span>) : <span className="text-xs text-stone-400">None</span>}</div></td>
                                            <td className="px-4 py-3 text-xs text-stone-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                                            <td className="px-4 py-3 text-xs text-stone-400">{u.last_login ? `${new Date(u.last_login).toLocaleDateString()} ${new Date(u.last_login).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Never'}</td>
                                            <td className="px-4 py-3 text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm">
                                                            <MoreHorizontal className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent className="w-56" align="end">
                                                        <DropdownMenuGroup>
                                                            <DropdownMenuItem onClick={() => { openEdit(u); }}>
                                                                <Edit3 className="w-4 h-4 mr-1" />Edit User
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => { handleViewAs(u); }}>
                                                                <Eye className="w-4 h-4 mr-1" />View As User
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => { setRepProfileUser(u); setShowRepProfile(true); }}>
                                                                <Briefcase className="w-4 h-4 mr-1" />Rep Profile
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => { setProfileMdUser(u); setShowProfileMd(true); }}>
                                                                <FileText className="w-4 h-4 mr-1" />AI Profile
                                                            </DropdownMenuItem>
                                                        </DropdownMenuGroup>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuGroup>
                                                            <DropdownMenuItem onClick={() => { setResetPwUser(u); setShowResetPw(true); }}>
                                                                <KeyRound className="w-4 h-4 mr-1" />Reset Password
                                                            </DropdownMenuItem>
                                                            {user?.user_id && u.user_id !== user.user_id && (
                                                                <DropdownMenuItem onClick={() => { setResetMfaUser(u); setResetMfaReason(''); setShowResetMfa(true); }}>
                                                                    <ShieldOff className="w-4 h-4 mr-1" />Reset two-step verification
                                                                </DropdownMenuItem>
                                                            )}
                                                            <DropdownMenuItem onClick={() => { toggleActive(u); }}>
                                                                {u.is_active ? <><Ban className="w-4 h-4 mr-1" />Suspend User</> : <><ShieldCheck className="w-4 h-4 mr-1" />Activate User</>}
                                                            </DropdownMenuItem>
                                                        </DropdownMenuGroup>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ─── Section 4: Invite Management ───────────────────────── */}
                {canManageMembers && (
                    <div className="bg-white rounded-xl border border-stone-200 p-5">
<h3 className="font-semibold text-stone-900 mb-1">Invite Team Member</h3>
                        <p className="text-xs text-stone-500 mb-4">They'll get an email with a single-use link to set their own password — you never choose one for them.</p>
                        <form onSubmit={handleInvite} className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-stone-600 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="colleague@company.com"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    data-testid="invite-email-input"
                                />
                            </div>
                            <div className="w-32">
                                <label className="block text-xs font-medium text-stone-600 mb-1">Role</label>
                                <select
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                >
                                    <option value="member">Member</option>
                                    <option value="org_admin">Org Admin</option>
                                </select>
                            </div>
                            <Button type="submit" disabled={inviting || !inviteEmail} className="h-10" data-testid="send-invite-btn">
                                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
                                Send Invite
                            </Button>
                        </form>

                        {/* Pending Invites */}
                        {invites.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-stone-100">
                                <h4 className="text-xs font-medium text-stone-500 mb-2">Pending Invites</h4>
                                <div className="space-y-2">
                                    {invites.map(invite => (
                                        <div key={invite.invite_id} className="flex items-center justify-between gap-2 p-2 bg-amber-50 rounded-lg">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Mail className="w-4 h-4 text-amber-600" />
                                                <span className="text-sm text-stone-700">{invite.email}</span>
                                                <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">{invite.role === 'org_admin' ? 'Org Admin' : 'Member'}</span>
                                                {invite.email_sent ? (
                                                    <span className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" data-testid={`invite-email-sent-${invite.invite_id}`}>Email sent</span>
                                                ) : (
                                                    <span className="text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded" data-testid={`invite-email-pending-${invite.invite_id}`}>Delivery not confirmed</span>
                                                )}
                                                {invite.expires_at && (
                                                    <span className="text-xs text-stone-400">Expires {new Date(invite.expires_at).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    onClick={() => handleResendInvite(invite.invite_id)}
                                                    disabled={resendingId === invite.invite_id}
                                                    className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-50"
                                                    data-testid={`resend-invite-${invite.invite_id}`}
                                                    title="Resend invitation email"
                                                >
                                                    {resendingId === invite.invite_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                                </button>
                                                <button
                                                    onClick={() => cancelInvite(invite.invite_id)}
                                                    className="p-1 text-stone-400 hover:text-red-500"
                                                    data-testid={`revoke-invite-${invite.invite_id}`}
                                                    title="Revoke invite"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Section 4b: AI privacy (AI Policy §3 / §8) ─────────── */}
                {/*
                    Here rather than in user AI-Assistant settings because §8 says
                    "in the organization settings" and "A Customer administrator" —
                    this is a tenant-wide control, not a per-user preference.
                */}
                {canManageMembers && (
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-stone-700" />
                            <h3 className="font-semibold text-stone-900">AI &amp; data privacy</h3>
                        </div>
                        <div className="p-5">
                            <AiPrivacyControls />
                        </div>
                    </div>
                )}

                {/* ─── Section 4c: Contact Owner changes (#863) ───────────── */}
                {canManageMembers && (
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-stone-700" />
                            <h3 className="font-semibold text-stone-900">Contact Owner changes</h3>
                        </div>
                        <div className="p-5">
                            <OwnerQuotaTimezoneSetting />
                        </div>
                    </div>
                )}

                {/* ─── Section 5: Custom Domains ─────────────────────────── */}
                {canManageMembers && (
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-stone-700" />
                                <h3 className="font-semibold text-stone-900">Custom Domains</h3>
                            </div>
                            <span className="text-xs text-stone-500">{domains.length} domain{domains.length !== 1 ? 's' : ''}</span>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Add Domain Form */}
                            <form onSubmit={handleAddDomain} className="flex items-end gap-3" data-testid="add-domain-form">
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-stone-600 mb-1">Domain Name</label>
                                    <input
                                        type="text"
                                        value={newDomain}
                                        onChange={(e) => setNewDomain(e.target.value)}
                                        placeholder="sales.yourcompany.com"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                        data-testid="add-domain-input"
                                    />
                                </div>
                                <Button type="submit" disabled={addingDomain || !newDomain.trim()} className="h-10" data-testid="add-domain-btn">
                                    {addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                                    Add Domain
                                </Button>
                            </form>

                            {/* Domains List */}
                            {domains.length === 0 ? (
                                <div className="text-center py-8" data-testid="no-domains-placeholder">
                                    <Globe className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                                    <p className="text-sm text-stone-500">No custom domains added yet</p>
                                    <p className="text-xs text-stone-400 mt-1">Add a domain above to get started with branded links and emails</p>
                                </div>
                            ) : (
                                <div className="space-y-3" data-testid="domains-list">
                                    {domains.map(domain => (
                                        <div key={domain.domain_id} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`domain-card-${domain.domain_id}`}>
                                            {/* Domain Header Row */}
                                            <div className="px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${domain.status === 'active' ? 'bg-emerald-100' : domain.status === 'verified' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                                                        <Globe className={`w-4 h-4 ${domain.status === 'active' ? 'text-emerald-600' : domain.status === 'verified' ? 'text-emerald-500' : 'text-amber-500'}`} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-stone-900 truncate" data-testid={`domain-name-${domain.domain_id}`}>{domain.domain}</span>
                                                            {domain.is_primary && (
                                                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-stone-100 text-stone-900 rounded" data-testid={`domain-primary-badge-${domain.domain_id}`}>
                                                                    <Star className="w-3 h-3" /> Primary
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            {getDomainStatusBadge(domain.status)}
                                                            {domain.ssl_status === 'active' && (
                                                                <span className="text-xs text-emerald-600 flex items-center gap-0.5">
                                                                    <Shield className="w-3 h-3" /> SSL
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    {domain.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs"
                                                                onClick={() => setExpandedDomainId(expandedDomainId === domain.domain_id ? null : domain.domain_id)}
                                                                data-testid={`domain-instructions-toggle-${domain.domain_id}`}
                                                            >
                                                                {expandedDomainId === domain.domain_id ? 'Hide' : 'Setup'}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                className="h-8 text-xs"
                                                                onClick={() => handleVerifyDomain(domain.domain_id)}
                                                                disabled={verifyingId === domain.domain_id}
                                                                data-testid={`domain-verify-btn-${domain.domain_id}`}
                                                            >
                                                                {verifyingId === domain.domain_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                                                                Verify
                                                            </Button>
                                                        </>
                                                    )}
                                                    {domain.status === 'active' && !domain.is_primary && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 text-xs"
                                                            onClick={() => handleSetPrimary(domain.domain_id)}
                                                            data-testid={`domain-set-primary-btn-${domain.domain_id}`}
                                                        >
                                                            <Star className="w-3.5 h-3.5 mr-1" /> Set Primary
                                                        </Button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteDomain(domain.domain_id, domain.domain)}
                                                        className="p-1.5 text-stone-400 hover:text-red-500 transition-colors"
                                                        data-testid={`domain-delete-btn-${domain.domain_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expanded Verification Instructions */}
                                            {expandedDomainId === domain.domain_id && domain.status === 'pending' && (
                                                <div className="px-4 py-3 bg-stone-50 border-t border-stone-200" data-testid={`domain-instructions-${domain.domain_id}`}>
                                                    <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">DNS Verification Instructions</h4>
                                                    <p className="text-xs text-stone-500 mb-3">Add a TXT record to your DNS provider to verify ownership of this domain.</p>
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                            <div>
                                                                <span className="text-xs text-stone-500 block">Record Type</span>
                                                                <span className="text-sm font-mono font-medium text-stone-900">TXT</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                            <div className="min-w-0 flex-1 mr-2">
                                                                <span className="text-xs text-stone-500 block">Host / Name</span>
                                                                <span className="text-sm font-mono font-medium text-stone-900 break-all" data-testid={`domain-record-name-${domain.domain_id}`}>_nurturecrm-verification</span>
                                                            </div>
                                                            <button onClick={() => copyToClipboard('_nurturecrm-verification')} className="p-1.5 text-stone-400 hover:text-stone-700 shrink-0" data-testid={`copy-record-name-${domain.domain_id}`}>
                                                                <ClipboardCopy className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                        <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                            <div className="min-w-0 flex-1 mr-2">
                                                                <span className="text-xs text-stone-500 block">Value</span>
                                                                <span className="text-sm font-mono font-medium text-stone-900 break-all" data-testid={`domain-record-value-${domain.domain_id}`}>{domain.verification_token}</span>
                                                            </div>
                                                            <button onClick={() => copyToClipboard(domain.verification_token)} className="p-1.5 text-stone-400 hover:text-stone-700 shrink-0" data-testid={`copy-record-value-${domain.domain_id}`}>
                                                                <ClipboardCopy className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {domain.error_message && (
                                                        <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid={`domain-error-${domain.domain_id}`}>
                                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                            <p className="text-xs text-red-700">{domain.error_message}</p>
                                                        </div>
                                                    )}
                                                    <p className="text-xs text-stone-400 mt-3">DNS changes typically take 5-30 minutes to propagate. Click "Verify" once your record is set.</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Modals (from TeamMembersTab) ─────────────────────────── */}

            {/* Edit user dialog — shared component (issue #558 follow-on).
                The add-mode usage was retired with POST /api/admin/users (#568):
                new users arrive through the invite form above. */}
            <UserFormDialog
                open={showEdit} onOpenChange={setShowEdit} mode="edit"
                form={editForm} setForm={setEditForm} groups={groups}
                onSubmit={handleEdit} editUser={editUser}
                onPillarChange={(orgRole, pillarRoles, isAgentDeveloper) => setEditForm(f => ({...f, org_role: orgRole, pillar_roles: pillarRoles, is_agent_developer: isAgentDeveloper, ...(computeEffectiveAccess(orgRole, pillarRoles).origin === 'org_role' ? { role: 'admin' } : {})}))}
            />

            {/* Reset Password Modal */}
            <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
                <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>{resetPwUser?.email}</DialogDescription></DialogHeader>
                    <div><Label>New Password *</Label><Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1" data-testid="reset-pw-input" /></div>
                    <DialogFooter><Button variant="outline" onClick={() => { setShowResetPw(false); setNewPassword(''); }}>Cancel</Button><Button onClick={handleResetPw} className="bg-stone-900 hover:bg-stone-800" data-testid="reset-pw-submit"><KeyRound className="w-4 h-4 mr-1" /> Reset</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Two-Step Verification Modal (issue #565) */}
            <Dialog open={showResetMfa} onOpenChange={v => { setShowResetMfa(v); if (!v) setResetMfaReason(''); }}>
                <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Reset two-step verification</DialogTitle><DialogDescription>{resetMfaUser?.email}</DialogDescription></DialogHeader>
                    <div className="text-sm text-stone-600 space-y-2">
                        <p>{resetMfaUser?.name || 'This user'} will be asked to set up two-step verification again the next time they sign in, with a new QR code. Their old authenticator and all ten recovery codes stop working.</p>
                        <p>Every existing session and remembered device is signed out immediately.</p>
                        <p className="text-stone-500">Verify who you are talking to before you do this. If <strong>you</strong> are the one locked out, this page cannot help — ask support to use the control-plane recovery path.</p>
                    </div>
                    <div><Label>Reason *</Label><Textarea value={resetMfaReason} onChange={e => setResetMfaReason(e.target.value)} placeholder="e.g. Lost phone, identity confirmed by video call" rows={3} className="mt-1" data-testid="reset-mfa-reason" /><p className="text-xs text-stone-400 mt-1">At least 10 characters. Recorded in the audit log.</p></div>
                    <DialogFooter><Button variant="outline" onClick={() => { setShowResetMfa(false); setResetMfaReason(''); }}>Cancel</Button><Button onClick={handleResetMfa} disabled={resettingMfa || resetMfaReason.trim().length < 10} className="bg-red-600 hover:bg-red-700" data-testid="reset-mfa-submit"><ShieldOff className="w-4 h-4 mr-1" /> {resettingMfa ? 'Resetting...' : 'Reset'}</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rep Profile Modal */}
            <RepProfilePanel
                open={showRepProfile}
                onOpenChange={setShowRepProfile}
                userId={repProfileUser?.user_id}
                userName={repProfileUser?.name}
            />

            {/* User Profile MD Modal */}
            <UserProfileMdPanel
                open={showProfileMd}
                onOpenChange={setShowProfileMd}
                userId={profileMdUser?.user_id}
                userName={profileMdUser?.name}
            />
        </AdminLayout>
    );
}
